import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CircleAlert, Eye, EyeOff } from 'lucide-react';
import { safeReturnTo } from '../lib/returnTo';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import BatchtrueMark from '@/components/assay/BatchtrueMark';
import { toast } from 'sonner';

const ACCESS_NOTES = [
  {
    k: '01',
    t: 'Free guest account',
    d: 'A guest account browses the full catalog and every wholesale price, and can build a cart.',
  },
  {
    k: '02',
    t: 'Wholesale approval to purchase',
    d: 'Ordering unlocks once your wholesale account is approved. Guest accounts cannot place orders.',
  },
  {
    k: '03',
    t: 'Ordering by the kit',
    d: 'Catalog quantities are kits. One kit is ten vials, and figures are shown per kit.',
  },
];

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    if (!email.trim() || !password) {
      setFormError('Enter your email address and password.');
      return;
    }
    setLoading(true);
    setFormError('');
    try {
      const user = await login(email.trim(), password);
      toast.success('Welcome back');

      let returnTo = null;
      try {
        returnTo = sessionStorage.getItem('returnTo');
      } catch {}
      try { sessionStorage.removeItem('returnTo'); } catch {}
      const destination = safeReturnTo(location.state?.returnTo || returnTo, user.role);
      if (destination) {
        navigate(destination, { replace: true });
        return;
      }

      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/catalog');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : error.response
        ? 'We could not sign you in. Check your details and try again.'
        : 'We could not reach the sign-in service. Check your connection and try again.';
      setFormError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-3 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label="Batchtrue Wholesale home"
          >
            <BatchtrueMark className="h-9 w-9 text-primary" />
            <span className="min-w-0">
              <span className="block font-display text-base font-semibold tracking-tight">Batchtrue Wholesale</span>
              <span className="label-quiet block">Wholesale ordering portal</span>
            </span>
          </button>
          <Button variant="ghost" onClick={() => navigate('/')} className="whitespace-nowrap text-muted-foreground">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Home
          </Button>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="mx-auto grid max-w-6xl gap-8 px-4 py-6 sm:px-6 lg:grid-cols-[1fr_minmax(0,26rem)] lg:gap-20 lg:py-20">
        <section className="load-in load-in-1 order-2 max-w-xl lg:order-1">
          <p className="label-eyebrow">Account access</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl">Sign in to the ordering portal.</h1>
          <p className="mt-5 max-w-prose text-[0.95rem] leading-relaxed text-muted-foreground">
            Batchtrue Wholesale is a private ordering portal. A free guest account can browse the catalog and pricing.
            Wholesale approval is required to purchase.
          </p>

          <dl className="mt-10 border-t border-border">
            {ACCESS_NOTES.map((note) => (
              <div key={note.k} className="grid grid-cols-[2.5rem_1fr] gap-4 border-b border-border py-5">
                <dt className="font-mono text-xs text-faint">{note.k}</dt>
                <dd>
                  <p className="text-sm font-medium">{note.t}</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{note.d}</p>
                </dd>
              </div>
            ))}
          </dl>

          <p className="mt-8 text-xs leading-relaxed text-faint">
            For research purposes only. Not for human consumption.
          </p>
        </section>

        <section className="order-1 w-full lg:order-2">
          <div className="border border-border bg-card p-6 sm:p-8">
            <p className="label-quiet">Sign in</p>
            <h2 className="mt-2 font-display text-2xl">Welcome back.</h2>

            <form onSubmit={handleSubmit} className="mt-7 space-y-5" noValidate>
              <div>
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (formError) setFormError('');
                  }}
                  placeholder="you@example.com"
                  autoComplete="email"
                  inputMode="email"
                  className="mt-2 h-11 bg-background text-base"
                  data-testid="login-email"
                  aria-describedby={formError ? 'login-error' : undefined}
                  aria-invalid={Boolean(formError)}
                  required
                />
              </div>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                  <Link to="/forgot-password" className="whitespace-nowrap text-xs font-medium text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    if (formError) setFormError('');
                  }}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="mt-2 h-11 bg-background pr-12 text-base"
                  data-testid="login-password"
                  aria-describedby={formError ? 'login-error' : undefined}
                  aria-invalid={Boolean(formError)}
                  required
                />
                <button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} onClick={() => setShowPassword(value => !value)} className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                </button>
                </div>
              </div>

              {formError ? (
                <div
                  id="login-error"
                  role="alert"
                  className="flex items-start gap-2 border border-destructive/40 bg-destructive/5 p-3 text-sm leading-relaxed text-destructive"
                >
                  <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{formError}</span>
                </div>
              ) : null}

              <Button type="submit" className="h-11 w-full text-sm" disabled={loading} data-testid="login-submit">
                {loading ? 'Signing in...' : 'Sign in'}
                {!loading ? <ArrowRight className="ml-1 h-4 w-4" /> : null}
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Need an account?{' '}
            <Link to="/register" className="font-medium text-primary hover:underline">
              Request access
            </Link>
          </p>
        </section>
      </main>
    </div>
  );
}
